import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { ApiService } from './api.service';
import { Store } from '../store';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { OrderSearchParams } from '../data-types/search-params';
import { ApiAuthHttpClient } from '../http/ApiAuthHttpClient';
import { OrderLineStatus } from '../models/order-line-status';
import { StatusGroup } from '../state';

@Injectable({
    providedIn: 'root',
})
export class OrdersService {
    env = environment;

    constructor(private apiService: ApiService, private store: Store, private http: ApiAuthHttpClient) {}

    public setPoNumber(card) {
        return this.http.put(this.env.apiPath + 'orders/line/po', card, this.apiService.getHttpOptions()).pipe(
            map((data: HttpErrorResponse) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    public setInvNumber(card) {
        return this.http.put(this.env.apiPath + 'orders/line/inv', card, this.apiService.getHttpOptions()).pipe(
            map((data: HttpErrorResponse) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    findOne(id) {
        return this.http.get(this.env.apiPath + 'orders/' + id).pipe(map((data: any) => data));
    }

    search(params) {
        const dataToSend = {
            params,
        };
        return this.http.post(this.env.apiPath + 'orders/search', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    updateTrackingId(orderDetailId, trackingId) {
        const dataToSend = {
            trackingId,
            orderDetailId,
        };
        return this.http
            .post(this.env.apiPath + 'orders/orderdetail/tracking', dataToSend)
            .pipe(map((data: any) => data));
    }

    bookSampleWithRoyalMail(orderDetailId: string, weightInGrams?: number) {
        return this.http
            .post(this.env.apiPath + `orders/samples/${orderDetailId}/royal-mail/book`, { weightInGrams })
            .pipe(map((data: any) => data.data));
    }

    bookSampleParcelWithRoyalMail(orderDetailIds: string[], weightInGrams?: number) {
        return this.http
            .post(this.env.apiPath + 'orders/samples/royal-mail/book', { orderDetailIds, weightInGrams })
            .pipe(map((data: any) => data.data));
    }

    getSampleRoyalMailLabel(orderDetailId: string) {
        return this.http.get(
            this.env.apiPath + `orders/samples/${orderDetailId}/royal-mail/label`,
            { responseType: 'arraybuffer' }
        );
    }

    uploadSampleDispatchPhoto(orderDetailId: string, file: File, note = '') {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('note', note);

        return this.http
            .post(this.env.apiPath + `orders/samples/${orderDetailId}/dispatch-photo`, formData)
            .pipe(map((data: any) => data.data));
    }

    getSampleDispatchPhotos(orderDetailId: string) {
        return this.http
            .get(this.env.apiPath + `orders/samples/${orderDetailId}/dispatch-photos`)
            .pipe(map((data: any) => data.data));
    }

    assignUser(data) {
        const dataToSend = {
            data,
        };
        return this.http
            .post(this.env.apiPath + 'orders/orderdetail/assign', dataToSend)
            .pipe(map((result: any) => result));
    }

    attachAddressToOrderDetail(addressId, orderDetail) {
        const dataToSend = {
            addressId,
            orderDetail,
        };
        return this.http
            .put(this.env.apiPath + 'orders/orderdetail/address', dataToSend)
            .pipe(map((data: any) => data));
    }

    identifyCourier(orderDetailId: string): Observable<any> {
        return this.http.get(`${this.env.apiPath}orders/orderdetail/identify-courier/${orderDetailId}`)
    }

    attachShipmentToOrderDetail(dataToSend) {
        return this.http
            .put(this.env.apiPath + 'orders/orderdetail/shipment', dataToSend)
            .pipe(map((data: any) => data));
    }

    searchOrderDetails(params: OrderSearchParams) {
        const dataToSend = {
            params,
        };
        return this.http.post(this.env.apiPath + 'orders/search/orderdetails', dataToSend).pipe(
            map((data: any) => data.data),
            tap((data: any) => {
                if (params.dashboard) {
                    if (params.isSample) {
                        this.store.set('samplesDashboard', data.data);
                    } else {
                        this.store.set('ordersDashboard', data.data);
                    }
                } else {
                    if (!params.isSample) {
                        this.store.set('samples', data.data);
                    } else {
                        this.store.set('orders', data.data);
                    }
                }
            })
        );
    }

    searchOrderDetailBoard(params, onlySkeleton = false) {
        const dataToSend = {
            params,
        };
        if (onlySkeleton === false) {
            // when the page is reloaded f.e. from order navigate to orders
            this.store.set('orderDetailBoard', undefined);
        }
        return this.http.post(
            this.env.apiPath + `orders/search/orderdetail/board${onlySkeleton === true ? '/true' : ''}`,
            dataToSend
        );
    }
    searchOrderLines(params, onlySkeleton = false) {
        const dataToSend = {
            params,
        };

        return this.http.post(this.env.apiPath + `orders/search/orderdetail/board?cardLoadLimit=0`, dataToSend);
    }
    getOrderCardsDetailsBatch(cardIds: Array<any>) {
        return this.http.post(this.env.apiPath + 'orders/order/lines/detail', cardIds);
    }
    getOrderLineDetails(lineId: string) {
        return this.http.get(this.env.apiPath + `orders/order/line/${lineId}/detail`);
    }

    cancelOrderLine(id: string, orderId: string, reason: any, cancelReward: boolean): Observable<any> {
        return this.http
            .put(
                this.env.apiPath + 'orders/orderdetail/cancel',
                {
                    id,
                    orderId,
                    reason,
                    cancelReward,
                },
                this.apiService.getHttpOptions()
            )
            .pipe(catchError(this.apiService.handleError));
    }

    createReason(name: string): Observable<any> {
        return this.http
            .post(
                this.env.apiPath + 'onhold-reason',
                {
                    name,
                },
                this.apiService.getHttpOptions()
            )
            .pipe(catchError(this.apiService.handleError));
    }
    putOnHold(id: string, orderId: string, reason: any): Observable<any> {
        return this.http
            .put(
                this.env.apiPath + 'orders/orderdetail/hold',
                {
                    id,
                    orderId,
                    reason,
                },
                this.apiService.getHttpOptions()
            )
            .pipe(catchError(this.apiService.handleError));
    }
    releaseHold(id: string, orderId: string): Observable<any> {
        return this.http.put(this.env.apiPath + 'orders/orderdetail/release', {
            id,
            orderId,
        });
    }
    getCancellationReasons() {
        return this.http.get(this.env.apiPath + 'cancellation-reason/public/1');
    }

    getHoldReasons() {
        return this.http.get(this.env.apiPath + 'onhold-reason');
    }

    restoreOrderLine(id, orderId) {
        const dataToSend = {
            id,
            orderId,
        };
        return this.http
            .put(this.env.apiPath + 'orders/orderdetail/restore', dataToSend, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    updateSupplier(orderDetail) {
        const dataToSend = {
            orderDetail,
        };
        return this.http
            .put(this.env.apiPath + 'orders/update/supplier', dataToSend, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    update(order) {
        return this.http.put(this.env.apiPath + 'orders/update', { order });
    }

    updateAddress(orderId, address, type) {
        const dataToSend = {
            orderId,
            address,
            type,
        };
        return this.http.put(this.env.apiPath + 'orders/address', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    requestUpdate(request) {
        const dataToSend = {
            request,
            user: this.store.selectForLocal('user'),
        };
        return this.http
            .post(this.env.apiPath + 'orders/requestupdate', dataToSend, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    getRequestReasons() {
        return this.http.get(this.env.apiPath + 'update-request-reason', this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    public getOrderLineStatuses(payload?: any): Observable<{ data: OrderLineStatus[] }> {
        return this.http
            .get(
                this.env.apiPath + 'orders/line/statuses' + `?${new URLSearchParams(payload || {}).toString()}`,
                this.apiService.getHttpOptions()
            )
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }
    getOrderLineStatusGroups(params?: any): Observable<{ data: StatusGroup[] }> {
        return this.http.get<{ data: StatusGroup[] }>(
            environment.apiPath +
                'orders/line/status-groups' +
                `?${new URLSearchParams((params as any) || {}).toString()}`,
            this.apiService.getHttpOptions()
        );
    }
    updateOrderLineStatus(orderLine) {
        const dataToSend = {
            orderLine,
            user: this.store.selectForLocal('user'),
        };
        return this.http.put(this.env.apiPath + 'orders/line/status', dataToSend).pipe(map((data: any) => data));
    }

    markAsDelivered(orderLine, status) {
        const dataToSend = {
            orderLine,
            status,
            user: this.store.selectForLocal('user'),
        };
        return this.http
            .put(this.env.apiPath + 'orders/line/delivered', dataToSend, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    updateDeliveryInstructions(data) {
        return this.http
            .put(this.env.apiPath + 'orders/update/deliveryinstructions', data, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }
}
